import { DocumentPagesSource } from '~/api/graphql';

type PageFile = { id: string; name: string };

export type ExportDesign = {
  name: string;
  includedPages: {
    before: { fileId: string; printable: boolean; file?: { name: string } | null }[];
    after: { fileId: string; printable: boolean; file?: { name: string } | null }[];
  };
};

export type ExportTemplate = {
  title: string;
  startPages: DocumentPagesSource;
  endPages: DocumentPagesSource;
  prefixFiles?: PageFile[] | null;
  suffixFiles?: PageFile[] | null;
};

export type ExportPage = { fileId: string; name: string };

/**
 * One end of the document: where its pages come from, and the pages themselves in print order.
 * A design's end whose design the client cannot read has an origin of null and no pages: the
 * server still prints them, so the choice is shown without names.
 */
export type ExportEnd = {
  source: 'design' | 'template' | 'none';
  /** Name of the design or template the pages come from. Null when nothing prints or unknown. */
  origin: string | null;
  pages: ExportPage[];
};

/** A design the document renders with that the client has no pages for. */
export const UNRESOLVED_DESIGN = 'unresolved';
export type ExportDesignRef = ExportDesign | typeof UNRESOLVED_DESIGN | null;

const NOTHING: ExportEnd = { source: 'none', origin: null, pages: [] };

const designEnd = (design: ExportDesignRef, end: 'before' | 'after'): ExportEnd => {
  if (design === UNRESOLVED_DESIGN) return { source: 'design', origin: null, pages: [] };
  const pages = (design?.includedPages[end] ?? [])
    .filter((page) => page.printable)
    .map((page) => ({ fileId: page.fileId, name: page.file?.name ?? 'Untitled PDF' }));
  return design && pages.length > 0 ? { source: 'design', origin: design.name, pages } : NOTHING;
};

const templateEnd = (template: ExportTemplate, files?: PageFile[] | null): ExportEnd =>
  files && files.length > 0
    ? {
        source: 'template',
        origin: template.title,
        pages: files.map((file) => ({ fileId: file.id, name: file.name }))
      }
    : NOTHING;

const endFrom = (
  source: DocumentPagesSource,
  design: ExportDesignRef,
  template: ExportTemplate,
  end: 'before' | 'after'
): ExportEnd => {
  switch (source) {
    case DocumentPagesSource.Design:
      return designEnd(design, end);
    case DocumentPagesSource.Template:
      return templateEnd(template, end === 'before' ? template.prefixFiles : template.suffixFiles);
    default:
      return NOTHING;
  }
};

/**
 * A best-effort summary of the pages an export prints around a document, for display only: the
 * server decides what prints. A document without a template prints its design's pages at both
 * ends, and one with a template prints, at each end, whatever that end's choice on the template
 * names.
 */
export const exportPages = (
  design: ExportDesignRef,
  template: ExportTemplate | null
): { start: ExportEnd; end: ExportEnd } =>
  template
    ? {
        start: endFrom(template.startPages, design, template, 'before'),
        end: endFrom(template.endPages, design, template, 'after')
      }
    : { start: designEnd(design, 'before'), end: designEnd(design, 'after') };
